<template>
  <EdroneTooltip
    :disabled="!$props.tooltip || disabled"
    :helper="$props.tooltip"
    :offset="[0, 8]"
    position="top"
    trigger-event="mouseenter"
  >
    <div :class="[$options.name, statusClassList]">
      <span v-if="$slots.icon && !$props.text" :class="`${$options.name}__icon`">
        <slot name="icon" />
      </span>
      <span v-else class="tw:flex tw:gap-1 tw:items-center">
        <span v-if="$slots.icon" :class="`${$options.name}__icon`">
          <slot name="icon" />
        </span>
        <span :class="[`${$options.name}__text`, { 'tw:first-letter:uppercase': upperCase }]">
          {{ text }}
        </span>
      </span>
    </div>
  </EdroneTooltip>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import type { PropType } from "vue";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import { BADGE_STATUS_TYPE } from "./EdroneBadgeStatus.constants";

export default defineComponent({
  name: "EdroneBadgeStatus",
  components: {
    EdroneTooltip,
  },
  props: {
    /* Text in status badge */
    text: {
      type: String,
      default: "",
      required: false,
    },
    /* Type of status badge (changes badge color and text color) */
    type: {
      // @TODO for backward compatibility, remove later and change all occurences
      type: String as PropType<
        | BADGE_STATUS_TYPE
        | "default"
        | "success"
        | "error"
        | "warn"
        | "info"
        | "violet"
        | "orange"
        | "purple"
        | "warning"
      >,
      default: BADGE_STATUS_TYPE.DEFAULT,
    },
    /* Size of badge (big or small) */
    size: {
      type: String as PropType<"small" | "big">,
      default: "small",
    },
    /* Invert badge and text colors */
    emphasis: {
      type: Boolean,
      default: false,
    },
    /* Disables badge (more bland colors) */
    disabled: {
      type: Boolean,
      default: false,
    },
    /* If text is entered, shows tooltip below the badge on mouse hover */
    tooltip: {
      type: String,
      default: null,
    },
    /* Uppercase first letter for badge text */
    upperCase: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { slots }) {
    const statusClassList = computed(() => {
      return {
        "EdroneBadgeStatus--default": props.type === "default",
        "EdroneBadgeStatus--success": props.type === "success",
        "EdroneBadgeStatus--warn": props.type === "warn" || props.type === "warning",
        "EdroneBadgeStatus--error": props.type === "error",
        "EdroneBadgeStatus--info": props.type === "info",
        "EdroneBadgeStatus--violet": props.type === "violet",
        "EdroneBadgeStatus--purple": props.type === "purple",
        "EdroneBadgeStatus--orange": props.type === "orange",
        "EdroneBadgeStatus--disabled": props.disabled,
        "EdroneBadgeStatus--emphasis": props.emphasis,
        "EdroneBadgeStatus--big": props.size === "big",
        "EdroneBadgeStatus--icon": slots.icon && !props.text,
      };
    });

    return {
      statusClassList,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneBadgeStatus {
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  text-align: center;
  transition: background-color ease-out 150ms;
  border: 1px solid transparent;
  white-space: nowrap;

  @apply tw:caption tw:h-5 tw:min-w-5 tw:leading-4 tw:m-0 tw:rounded-md tw:py-0.5 tw:px-1.5;
}

.EdroneBadgeStatus__icon {
  font-size: 12px;
  width: 12px;
  height: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.EdroneBadgeStatus--big {
  @apply tw:leading-5 tw:m-0 tw:rounded-xl tw:py-1.5 tw:px-3 tw:h-8 tw:min-w-8;
}

.EdroneBadgeStatus--big .EdroneBadgeStatus__icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.EdroneBadgeStatus--default {
  @apply tw:bg-default-000 tw:border-default-200;
}

.EdroneBadgeStatus--default .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--default .EdroneBadgeStatus__text {
  @apply tw:text-default-800;
}

.EdroneBadgeStatus--default.EdroneBadgeStatus--emphasis {
  @apply tw:bg-default-900 tw:border-default-900;
}

.EdroneBadgeStatus--default.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--default.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  color: white;
}

.EdroneBadgeStatus--default.EdroneBadgeStatus--disabled {
  @apply tw:bg-default-000 tw:border-default-200;
}

.EdroneBadgeStatus--default.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--default.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-default-500;
}

.EdroneBadgeStatus--default.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-default-100 tw:border-default-100;
}

.EdroneBadgeStatus--default.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--default.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  color: white;
}

.EdroneBadgeStatus--success {
  @apply tw:bg-green-000 tw:border-green-200;
}

.EdroneBadgeStatus--success .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--success .EdroneBadgeStatus__text {
  @apply tw:text-green-800;
}

.EdroneBadgeStatus--success.EdroneBadgeStatus--emphasis {
  @apply tw:bg-green-500 tw:border-green-500;
}

.EdroneBadgeStatus--success.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--success.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  color: white;
}

.EdroneBadgeStatus--success.EdroneBadgeStatus--disabled {
  @apply tw:bg-green-000 tw:border-green-200;
}

.EdroneBadgeStatus--success.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--success.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-green-200;
}

.EdroneBadgeStatus--success.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-green-100 tw:border-green-100;
}

.EdroneBadgeStatus--success.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--success.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--warn {
  @apply tw:bg-yellow-000 tw:border-yellow-200;
}

.EdroneBadgeStatus--warn .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--warn .EdroneBadgeStatus__text {
  @apply tw:text-yellow-800;
}

.EdroneBadgeStatus--warn.EdroneBadgeStatus--emphasis {
  @apply tw:bg-yellow-500 tw:border-yellow-500;
}

.EdroneBadgeStatus--warn.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--warn.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--warn.EdroneBadgeStatus--disabled {
  @apply tw:bg-yellow-000 tw:border-yellow-200;
}

.EdroneBadgeStatus--warn.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--warn.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-yellow-200;
}

.EdroneBadgeStatus--warn.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-yellow-100 tw:border-yellow-100;
}

.EdroneBadgeStatus--warn.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--warn.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--error {
  @apply tw:bg-red-000 tw:border-red-200;
}

.EdroneBadgeStatus--error .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--error .EdroneBadgeStatus__text {
  @apply tw:text-red-800;
}

.EdroneBadgeStatus--error.EdroneBadgeStatus--emphasis {
  @apply tw:bg-red-500 tw:border-red-500;
}

.EdroneBadgeStatus--error.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--error.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--error.EdroneBadgeStatus--disabled {
  @apply tw:bg-red-000 tw:border-red-200;
}

.EdroneBadgeStatus--error.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--error.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-red-200;
}

.EdroneBadgeStatus--error.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-red-100 tw:border-red-100;
}

.EdroneBadgeStatus--error.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--error.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--info {
  @apply tw:bg-blue-000 tw:border-blue-200;
}

.EdroneBadgeStatus--info .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--info .EdroneBadgeStatus__text {
  @apply tw:text-blue-800;
}

.EdroneBadgeStatus--info.EdroneBadgeStatus--emphasis {
  @apply tw:bg-blue-500 tw:border-blue-500;
}

.EdroneBadgeStatus--info.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--info.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--info.EdroneBadgeStatus--disabled {
  @apply tw:bg-blue-000 tw:border-blue-200;
}

.EdroneBadgeStatus--info.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--info.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-blue-200;
}

.EdroneBadgeStatus--info.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-blue-100 tw:border-blue-100;
}

.EdroneBadgeStatus--info.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--info.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--violet {
  @apply tw:bg-violet-000 tw:border-violet-200;
}

.EdroneBadgeStatus--violet .EdroneBadgeStatus__icon {
  @apply tw:text-violet-500;
}

.EdroneBadgeStatus--violet .EdroneBadgeStatus__text {
  @apply tw:text-violet-800;
}

.EdroneBadgeStatus--violet.EdroneBadgeStatus--emphasis {
  @apply tw:bg-violet-500 tw:border-violet-500;
}

.EdroneBadgeStatus--violet.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--violet.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--violet.EdroneBadgeStatus--disabled {
  @apply tw:bg-violet-100 tw:border-violet-100;
}

.EdroneBadgeStatus--violet.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--violet.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-violet-200;
}

.EdroneBadgeStatus--violet.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-violet-100 tw:border-violet-100;
}

.EdroneBadgeStatus--violet.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--violet.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--purple {
  @apply tw:bg-purple-000 tw:border-purple-200;
}

.EdroneBadgeStatus--purple .EdroneBadgeStatus__icon {
  @apply tw:text-purple-500;
}

.EdroneBadgeStatus--purple .EdroneBadgeStatus__text {
  @apply tw:text-purple-800;
}

.EdroneBadgeStatus--purple.EdroneBadgeStatus--emphasis {
  @apply tw:bg-purple-500 tw:border-purple-500;
}

.EdroneBadgeStatus--purple.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--purple.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--purple.EdroneBadgeStatus--disabled {
  @apply tw:bg-purple-100 tw:border-purple-100;
}

.EdroneBadgeStatus--purple.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--purple.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-purple-200;
}

.EdroneBadgeStatus--purple.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-purple-100 tw:border-purple-100;
}

.EdroneBadgeStatus--purple.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--purple.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--orange {
  @apply tw:bg-orange-000 tw:border-orange-200;
}

.EdroneBadgeStatus--orange .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--orange .EdroneBadgeStatus__text {
  @apply tw:text-orange-800;
}

.EdroneBadgeStatus--orange.EdroneBadgeStatus--emphasis {
  @apply tw:bg-orange-500 tw:border-orange-500;
}

.EdroneBadgeStatus--orange.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--orange.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--orange.EdroneBadgeStatus--disabled {
  @apply tw:bg-orange-000 tw:border-orange-200;
}

.EdroneBadgeStatus--orange.EdroneBadgeStatus--disabled .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--orange.EdroneBadgeStatus--disabled .EdroneBadgeStatus__text {
  @apply tw:text-orange-200;
}

.EdroneBadgeStatus--orange.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis {
  @apply tw:bg-orange-100 tw:border-orange-100;
}

.EdroneBadgeStatus--orange.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__icon,
.EdroneBadgeStatus--orange.EdroneBadgeStatus--disabled.EdroneBadgeStatus--emphasis .EdroneBadgeStatus__text {
  @apply tw:text-white;
}

.EdroneBadgeStatus--icon {
  padding: 0.25rem;
  width: 1.25rem;
}

.EdroneBadgeStatus--icon.EdroneBadgeStatus--big {
  padding: 0.5rem;
  width: 2rem;
}
</style>
